import { useCallback, useEffect, useState } from 'react';

import { deployPipeline, undeployPipeline } from '../pipelineDeployment';
import createPipeline from '../pipelineCreation';

export const usePipelineDeployment = () => {
  const deploy = useCallback(deployPipeline, []);
  const undeploy = useCallback(undeployPipeline, []);

  return {
    deploy, undeploy,
  }
};

// To be used for new Pipeline Creation only
export const usePipelineBuilder = (workspaceID, pipelineToCopy = null, pipelineStepsToCopy = [], pipelineRefetch = () => {}) => {
  const [pipelineName, setPipelineName] = useState(pipelineToCopy?.pipelineID ?? null);
  const [pipelineSteps, setPipelineSteps] = useState(pipelineStepsToCopy ?? []);

  useEffect(() => {
    // Soft checks to include both undefined and null
    if (!pipelineName && pipelineToCopy?.pipelineID) {
      setPipelineName(pipelineToCopy?.pipelineID)
    }

    // 0 or null are acceptable.
    if (!pipelineSteps?.length && pipelineStepsToCopy?.length) {
      setPipelineSteps([...pipelineStepsToCopy])
    }
  }, [pipelineName, pipelineSteps, pipelineToCopy, pipelineStepsToCopy])


  const addModelStep = useCallback((model) => {
    setPipelineSteps([
      ...pipelineSteps,
      {
        ModelInference: {
          models: [{
            name: model?.modelID,
            version: model?.modelVersion,
            sha: model?.sha,
          }]
        }
      },
    ])
  }, [pipelineSteps]);

  const build = useCallback(async (newPipelineSteps = pipelineSteps) => {
    try {
      const ret = await createPipeline(pipelineName, workspaceID, newPipelineSteps)
      pipelineRefetch(ret?.pipeline_pk_id, ret?.pipeline_variant_pk_id);
      setPipelineSteps(newPipelineSteps);
      return ret;
    } catch (error) {
      console.error('Error creating pipeline', error)
    }
  }, [pipelineSteps, pipelineName, workspaceID, pipelineRefetch]);

  // Stateful, not sent out to the API until Build is called by the user
  const removeStep = useCallback(async (index) => {
    const p = [...pipelineSteps];
    p.splice(index, 1);
    setPipelineSteps(p);
  }, [pipelineSteps]);

  return {
    pipelineName,
    pipelineSteps,
    setPipelineName,
    addStep: addModelStep,
    removeStep,
    setPipelineSteps,
    build,
  };
};

export const usePipelineStepRemover = (workspaceID, pipeline = null, pipelineSteps = [], pipelineRefetch = () => {}) => {
  const pipelineName = pipeline?.pipelineID;

  const build = useCallback(async (newPipelineSteps = pipelineSteps) => {
    try {
      const res = await createPipeline(pipelineName, workspaceID, newPipelineSteps)
      pipelineRefetch(res?.pipeline_pk_id, res?.pipeline_variant_pk_id);
    } catch (error) {
      console.error('Error creating pipeline', error)
    }
  }, [pipelineName, pipelineRefetch, pipelineSteps, workspaceID]);

  // Implies that the removing the step will result in a build request
  return async (index) => {
    const p = [...pipelineSteps]
    p.splice(index, 1)
    await build(p)
  };
};

